import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { jwtDecode } from 'jwt-decode';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrash, FaLock, FaUnlock, FaThumbtack, FaEdit, FaPlus, FaTimes } from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import { trackForumView } from '../utils/analytics';



function ThreadList() {
  const { forumId } = useParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forumName, setForumName] = useState('Threads');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingThread, setEditingThread] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const limit = 20;
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const [isPoll, setIsPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const insertBBCode = tag => setContent(prev => `${prev}[${tag}][/${tag}]`);
const handleEmojiClick = ({ emoji }) => setContent(prev => prev + emoji);
  const refreshThreads = async () => {
    try {
      const res = await axiosInstance.get(`/threads/${forumId}`);
      setThreads(res.data.threads);
    } catch (err) {
      console.error('Fehler beim Laden der Threads:', err);
    }
  };
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { title, content, isPoll  };
  
      if (isPoll) {
        payload.pollQuestion = pollQuestion;
        payload.pollOptions = pollOptions.filter(opt => opt.trim());
      }
  
      await axiosInstance.post(`/threads/${forumId}`, payload);
      setTitle('');
      setContent('');
      setIsPoll(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setShowForm(false);
      fetchThreads();
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Erstellen');
    }
  };
  useEffect(() => {
    refreshThreads();
  }, [forumId]);
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser(decoded);
      } catch (e) {
        console.error('Token ungültig', e);
      }
    }
  }, []);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/threads/${forumId}`, {
        params: { page, limit },
      });
      const allThreads = res.data.threads || [];
      setThreads([allThreads[0]]);
      setTimeout(() => setThreads(allThreads), 0);
      setTotalPages(res.data.totalPages || 1);
      setForumName(res.data.forumName || 'Threads');
      
      // Track forum view with title
      if (res.data.forumName) {
        trackForumView(forumId, res.data.forumName, page);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [forumId, page]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/threads/${id}`);
      fetchThreads();
    } catch {
      alert('Fehler beim Löschen');
    }
  };

  const handleToggleLock = async (id) => {
    try {
      await axiosInstance.patch(`/threads/${id}/toggle-lock`);
      fetchThreads();
    } catch {
      alert('Fehler beim Sperren/Entsperren');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      await axiosInstance.patch(`/threads/${id}/toggle-pin`);
      fetchThreads();
    } catch {
      alert('Fehler beim An-/Abpinnen');
    }
  };

  const handleEditTitle = async () => {
    try {
      await axiosInstance.patch(`/threads/${editingThread._id}`, { title: editTitle });
      setEditingThread(null);
      setEditTitle('');
      fetchThreads();
    } catch {
      alert('Fehler beim Speichern');
    }
  };

  const renderPageButtons = () => {
    const buttons = [];
    const maxButtons = 10;
    let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
    let endPage = startPage + maxButtons - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`btn btn-sm ${page === i ? 'bg-[#448f65] text-white' : 'bg-white text-[#448f65] border border-[#448f65]'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  // Helper function to generate thread URL based on read status
  const getThreadUrl = (thread) => {
    // For now, we'll use a simple heuristic:
    // If the user is logged in and the thread has been updated recently, 
    // use unread navigation, otherwise use last post navigation
    if (currentUser && thread.lastPostId) {
      // Check if this might be an unread thread
      const lastPostTime = new Date(thread.lastPostDate || thread.updatedAt);
      const now = new Date();
      const timeDiff = now - lastPostTime;
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      // If the thread was updated in the last 24 hours, assume it might be unread
      if (hoursDiff < 24) {
        return `/thread/${thread._id}#unread`;
      }
    }
    
    // Default to last post navigation
    return `/thread/${thread._id}?page=${thread.lastPostPage || 1}#post-${thread.lastPostId}`;
  };

  return (
    <div className="w-full px-2 sm:px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">{forumName}</h1>
        </div>
        {currentUser && (
          <button
            onClick={() => setShowForm(f => !f)}
            className="glass-btn w-full sm:w-auto py-3 sm:py-3 px-4 sm:px-6 text-white font-semibold rounded-xl shadow-glow hover:shadow-xl transition-all duration-200 flex items-center gap-2 text-base sm:text-lg"
            style={{ maxWidth: '260px' }}
          >
            {showForm ? (
              <>
                <FaTimes className="w-4 h-4" />
                Abbrechen
              </>
            ) : (
              <>
                <FaPlus className="w-4 h-4" />
                Thread erstellen
              </>
            )}
          </button>
        )}
      </div>

      {showForm && (
        <div className="modern-card p-2 sm:p-6 mb-8 animate-slide-up">
          <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">Neuen Thread erstellen</h3>
          
          <form onSubmit={handleCreate} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Titel
              </label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                placeholder="Worum soll es gehen?"
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Inhalt
              </label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                {['b', 'i', 'u', 's'].map(tag => (
                  <button 
                    type="button" 
                    key={tag} 
                    onClick={() => insertBBCode(tag)} 
                    className="btn btn-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-primary hover:text-white transition-all duration-200"
                  >
                    {tag}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="btn btn-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-primary hover:text-white transition-all duration-200" 
                  onClick={() => setShowEmojiPicker(s => !s)}
                >
                  😊
                </button>
              </div>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={6}
                required={!isPoll && content.trim() === ''}
                className="textarea textarea-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                placeholder="Teile deine Gedanken mit..."
              />
              {showEmojiPicker && (
                <div className="mt-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-medium">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
              )}
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <input 
                type="checkbox" 
                className="checkbox checkbox-primary mt-1" 
                checked={isPoll} 
                onChange={e => setIsPoll(e.target.checked)} 
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Als Umfrage erstellen
              </span>
            </div>

            {isPoll && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Umfragefrage
                  </label>
                  <input 
                    type="text" 
                    className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200" 
                    value={pollQuestion} 
                    onChange={e => setPollQuestion(e.target.value)} 
                    required 
                    placeholder="Was möchtest du fragen?"
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Antwortoptionen
                  </label>
                  {pollOptions.map((opt, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input 
                        type="text" 
                        className="input input-bordered w-full rounded-xl border-gray-300 focus:border-primary focus:ring-primary/20 transition-all duration-200" 
                        value={opt} 
                        onChange={e => {
                          const newOpts = [...pollOptions];
                          newOpts[idx] = e.target.value;
                          setPollOptions(newOpts);
                        }} 
                        required 
                        placeholder={`Option ${idx + 1}`}
                      />
                      {pollOptions.length > 2 && (
                        <button 
                          type="button" 
                          className="btn btn-sm btn-error rounded-xl" 
                          onClick={() => {
                            setPollOptions(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline rounded-xl" 
                    onClick={() => setPollOptions([...pollOptions, ''])}
                  >
                    + Option hinzufügen
                  </button>
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="glass-btn w-full py-3 px-4 rounded-xl text-white font-semibold text-lg shadow-glow hover:shadow-xl transition-all duration-200"
            >
              Thread erstellen
            </button>
          </form>
        </div>
      )}
   
 
      {editingThread && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-2">Titel bearbeiten</h3>
            <input
              type="text"
              className="input input-bordered w-full"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <div className="modal-action">
              <button className="btn btn-success" onClick={handleEditTitle}>Speichern</button>
              <button className="btn" onClick={() => setEditingThread(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center mt-6">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <AnimatePresence>
              {threads.map((thread, index) => (
                <motion.div
                  key={thread._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  className="modern-card p-2 sm:p-6 hover:scale-[1.01] transition-all duration-200 group"
                >
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Link 
                          to={getThreadUrl(thread)} 
                          className="text-xl font-bold text-gray-900 dark:text-white hover:text-primary transition-colors duration-200 group-hover:text-primary"
                        >
                          {thread.title}
                        </Link>
                        <div className="flex items-center gap-2">
                          {thread.pinned && (
                            <span className="bg-blue-100 dark:bg-blue-200 text-gray-900 dark:text-gray-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <FaThumbtack className="w-3 h-3 text-gray-900 dark:text-gray-800" />
                              Gepinnt
                            </span>
                          )}
                          {thread.locked && (
                            <span className="bg-yellow-100 dark:bg-yellow-200 text-gray-900 dark:text-gray-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <FaLock className="w-3 h-3 text-gray-900 dark:text-gray-800" />
                              Gesperrt
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <span className="font-medium">von {thread.author?.username || '–'}</span>
                        <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                        <span>{new Date(thread.createdAt).toLocaleString('de-DE', { 
                          dateStyle: 'short', 
                          timeStyle: 'short' 
                        })}</span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 text-right min-w-[200px]">
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                        Letzter Beitrag:
                      </div>
                      {thread.lastPostId ? (
                        <Link
                          to={`/thread/${thread._id}?page=${thread.lastPostPage || 1}#post-${thread.lastPostId}`}
                          className="hover:text-primary transition-colors duration-200 block"
                        >
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {thread.lastPostAuthor?.username || '–'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(thread.lastPostAt).toLocaleString('de-DE', { 
                              dateStyle: 'short', 
                              timeStyle: 'short' 
                            })}
                          </div>
                        </Link>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                          Noch keine Antworten
                        </div>
                      )}
                    </div>

                    {currentUser?.isAdmin && (
                      <div className="flex flex-wrap gap-2 lg:flex-col lg:w-auto">
                        <button 
                          className="btn btn-sm bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-200 rounded-lg" 
                          onClick={() => { setEditTitle(thread.title); setEditingThread(thread); }}
                        >
                          <FaEdit className="w-3 h-3" />
                        </button>
                        <button 
                          className="btn btn-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-700 border-yellow-200 rounded-lg" 
                          onClick={() => handleToggleLock(thread._id)}
                        >
                          {thread.locked ? <FaUnlock className="w-3 h-3" /> : <FaLock className="w-3 h-3" />}
                        </button>
                        <button 
                          className="btn btn-sm bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-200 rounded-lg" 
                          onClick={() => handleTogglePin(thread._id)}
                        >
                          <FaThumbtack className="w-3 h-3" />
                        </button>
                        <button 
                          className="btn btn-sm bg-red-100 hover:bg-red-200 text-red-700 border-red-200 rounded-lg" 
                          onClick={() => handleDelete(thread._id)}
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="flex justify-center mt-8 gap-2 flex-wrap">
            {renderPageButtons()}
          </div>
        </>
      )}
    </div>
  );
}

export default ThreadList;
